import { z } from "zod";

import { apiKeyActor, type ApiKey } from "./api-keys";
import { customerRequestFields } from "./customers";
import { DIRECTORY_QUERY_MAX_LENGTH, directoryEntrySchema, PARTY_KINDS } from "./directory";
import { DIRECTORY_RELATED_LIMIT } from "./directory-search";
import {
	createIntakeUploadRequestSchema,
	createIntakeUploadResponseSchema,
	intakeFileSchema,
	type IntakeFile,
	type IntakeUploader,
} from "./intake";
import { otherPartyRequestSchema } from "./other-parties";
import { endDateAfterSince, endDateNotBeforeSince, partyContactFields } from "./parties";
import { addPersonRequestSchema } from "./people";
import { vendorRequestFields } from "./vendors";

// Ravnar's API for integrations: what software sends, with one of an organization's API keys, to the routes under
// `/api/v1/` — `POST /api/v1/parties`; `/api/v1/documents`, which uploads a document to Intake and says where it
// stands; and the MCP server at `/api/v1/mcp`, whose tools take the same parties and documents. A party is what its
// own form sends — the same fields, refused in the same words — with every text field a form sends as "" when nobody
// filled it in free to be left out instead: software need not send a field it has nothing for. The API and its tests
// share these.

/** The contact block of a vendor, a customer and an "other" party, each field of which software may leave out. */
const contactFields = {
	contactName: partyContactFields.contactName.default(""),
	email: partyContactFields.email.default(""),
	phone: partyContactFields.phone.default(""),
	website: partyContactFields.website.default(""),
	country: partyContactFields.country.default(""),
};

/** A vendor's fields as software sends them: its form's, with every text the form sends empty free to be left out. */
const vendorFields = {
	...vendorRequestFields,
	...contactFields,
	legalName: vendorRequestFields.legalName.default(""),
	taxId: vendorRequestFields.taxId.default(""),
};

/** A customer's fields as software sends them, the same way. */
const customerFields = {
	...customerRequestFields,
	...contactFields,
	legalName: customerRequestFields.legalName.default(""),
	taxId: customerRequestFields.taxId.default(""),
	accountManager: customerRequestFields.accountManager.default(""),
};

/** An "other" party's fields as software sends them, the same way. */
const otherPartyFields = { ...otherPartyRequestSchema.shape, ...contactFields };

/** A person as software adds one: what People & HR's add form sends, its refinement kept. */
export const integrationPersonSchema = addPersonRequestSchema;

/**
 * A vendor, a customer and an "other" party as software adds one: their forms' fields, with the one rule that spans two
 * of them — no end date before the start — refined on again, since a field that may now be left out cannot be put over a
 * refined schema's own. The MCP server's add tools take these; `POST /api/v1/parties` takes the same with a `kind`.
 */
export const integrationVendorSchema = z.object(vendorFields).refine(endDateAfterSince, endDateNotBeforeSince);
export const integrationCustomerSchema = z.object(customerFields).refine(endDateAfterSince, endDateNotBeforeSince);
export const integrationOtherPartySchema = z.object(otherPartyFields).refine(endDateAfterSince, endDateNotBeforeSince);

/** What `POST /api/v1/parties` takes: one person, vendor, customer or "other" party, by its `kind`. */
export const integrationPartyRequestSchema = z.discriminatedUnion(
	"kind",
	[
		addPersonRequestSchema.safeExtend({ kind: z.literal("person") }),
		z.object({ kind: z.literal("vendor"), ...vendorFields }).refine(endDateAfterSince, endDateNotBeforeSince),
		z.object({ kind: z.literal("customer"), ...customerFields }).refine(endDateAfterSince, endDateNotBeforeSince),
		z.object({ kind: z.literal("other"), ...otherPartyFields }).refine(endDateAfterSince, endDateNotBeforeSince),
	],
	{ error: "Choose a kind: person, vendor, customer or other" },
);
export type IntegrationPartyRequest = z.infer<typeof integrationPartyRequestSchema>;

/** What `POST /api/v1/parties` answers: the kind of the party it added, and the id the API gave it. */
export const addedPartySchema = z.object({ kind: z.enum(PARTY_KINDS), id: z.string().min(1) });
export type AddedParty = z.infer<typeof addedPartySchema>;

/** The most parties whose name holds a search `search_directory` answers with. */
export const INTEGRATION_NAMED_LIMIT = 20;

/** What the MCP server's `search_directory` tool takes: what to look for, and the one kind to look among, if any. */
export const integrationDirectorySearchSchema = z.object({
	query: z
		.string()
		.trim()
		.min(1, "Say what to look for")
		.max(DIRECTORY_QUERY_MAX_LENGTH, "Use at most 80 characters"),
	kind: z.enum(PARTY_KINDS).optional(),
});
export type IntegrationDirectorySearch = z.infer<typeof integrationDirectorySearchSchema>;

/**
 * A party `search_directory` found: its directory row, which never holds a person's private details, without what only
 * the Directory's list works with, and with where it stands today in the list's own words (`partyStatus`).
 */
export const integrationFoundPartySchema = directoryEntrySchema
	.omit({ activeLabel: true, incomplete: true, projectedAt: true })
	.extend({ status: z.string().min(1).max(40) });
export type IntegrationFoundParty = z.infer<typeof integrationFoundPartySchema>;

/**
 * What `search_directory` answers: the parties whose name holds the search, A→Z, and then the ones the Directory's
 * knowledge base finds most like it that the first list does not hold, best first.
 */
export const integrationDirectoryFoundSchema = z.object({
	named: z.array(integrationFoundPartySchema).max(INTEGRATION_NAMED_LIMIT),
	related: z.array(integrationFoundPartySchema).max(DIRECTORY_RELATED_LIMIT),
});
export type IntegrationDirectoryFound = z.infer<typeof integrationDirectoryFoundSchema>;

/** A SHA-256 as software may send it: 64 hex digits in either case, kept in lowercase as Intake keeps a content's. */
const integrationSha256Schema = z
	.string()
	.trim()
	.toLowerCase()
	.regex(/^[\da-f]{64}$/, "Send the SHA-256 of the file's bytes as 64 hex digits");

/**
 * What `POST /api/v1/documents` and the MCP server's `upload_document` take: what the Intake page sends — the file's
 * name, with an extension Intake takes, and its size in bytes, which its upload must be exactly — and, if software has
 * it, the SHA-256 of its bytes: a document the organization already holds with that SHA-256, and that size, is then
 * answered as it is, and nothing is uploaded again. Intake hashes what arrives either way. A SHA-256 that is not the
 * bytes' therefore keeps Intake from recognizing them before they are sent — unless it is the SHA-256 of another
 * document of the organization, of the same size, which is then answered instead, and the bytes are never sent.
 */
export const integrationDocumentRequestSchema = createIntakeUploadRequestSchema.extend({
	sha256: integrationSha256Schema.optional(),
});
export type IntegrationDocumentRequest = z.infer<typeof integrationDocumentRequestSchema>;

/**
 * A document as the integration API answers it: its id, its name and size, where it stands (`INTAKE_FILE_STATUSES`),
 * when it was asked for, and the SHA-256 of its content once it is stored — and nothing of who uploaded it.
 */
export const integrationDocumentSchema = intakeFileSchema.pick({
	id: true,
	name: true,
	size: true,
	status: true,
	uploadedAt: true,
	sha256: true,
});
export type IntegrationDocument = z.infer<typeof integrationDocumentSchema>;

/** A file of the organization's Intake as the integration API answers it (`integrationDocumentSchema`). */
export function integrationDocumentOf({ id, name, size, status, uploadedAt, sha256 }: IntakeFile): IntegrationDocument {
	return { id, name, size, status, uploadedAt, ...(sha256 !== undefined && { sha256 }) };
}

/**
 * What `POST /api/v1/documents` answers, and the MCP server's `upload_document`: 201 with the new document, `pending`,
 * and the presigned URL to PUT exactly its bytes to before `expiresAt` — or 200 with the document the organization
 * already holds with the SHA-256 the request named, and `upload` null, since there is nothing to send.
 */
export const integrationDocumentUploadSchema = z.object({
	document: integrationDocumentSchema,
	upload: createIntakeUploadResponseSchema.shape.upload.nullable(),
});
export type IntegrationDocumentUpload = z.infer<typeof integrationDocumentUploadSchema>;

/** What the MCP server's `get_document` takes: the id of one of the organization's documents. */
export const integrationDocumentLookupSchema = z.object({
	documentId: z.string().trim().min(1, "Give the document's id").max(128, "Use at most 128 characters"),
});
export type IntegrationDocumentLookup = z.infer<typeof integrationDocumentLookupSchema>;

/** Who the files table says uploaded a document software uploaded with `key`: the key, as the audit log names it. */
export function apiKeyUploader(key: Pick<ApiKey, "id" | "name" | "lastFour">): IntakeUploader {
	return { id: key.id, name: apiKeyActor(key), apiKey: true };
}
